import type { IngestJobRow } from './mergeIngestJobs'

/** A top-level job in a run panel, optionally with PLC zip children nested under it. */
export interface NestedIngestJob {
  job: IngestJobRow
  children: IngestJobRow[]
}

export function isPlcZipUmbrella(job: IngestJobRow): boolean {
  if (job.kind !== 'PLC') return false
  if (job.fileType === 'zip') return true
  return /\.zip$/i.test(job.fileName)
}

function bySubmittedAsc(a: IngestJobRow, b: IngestJobRow): number {
  return new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime()
}

/**
 * Within one run's flat job list, nest per-file PLC jobs under their zip
 * umbrella. Non-PLC jobs (and PLC jobs with no zip umbrella in the same
 * group) stay top-level peers.
 */
export function nestPlcZipJobs(jobs: IngestJobRow[]): NestedIngestJob[] {
  const umbrellaByGroup = new Map<string, IngestJobRow>()
  for (const job of jobs) {
    if (!isPlcZipUmbrella(job)) continue
    const groupId = job.groupId?.trim()
    if (!groupId) continue
    const existing = umbrellaByGroup.get(groupId)
    if (!existing || bySubmittedAsc(job, existing) < 0) {
      umbrellaByGroup.set(groupId, job)
    }
  }

  const childrenByUmbrellaId = new Map<string, IngestJobRow[]>()
  const nestedChildIds = new Set<string>()
  for (const job of jobs) {
    if (job.kind !== 'PLC' || isPlcZipUmbrella(job)) continue
    const groupId = job.groupId?.trim()
    if (!groupId) continue
    const umbrella = umbrellaByGroup.get(groupId)
    if (!umbrella) continue
    const list = childrenByUmbrellaId.get(umbrella.id) ?? []
    list.push(job)
    childrenByUmbrellaId.set(umbrella.id, list)
    nestedChildIds.add(job.id)
  }

  const nested: NestedIngestJob[] = []
  for (const job of jobs) {
    if (nestedChildIds.has(job.id)) continue
    if (isPlcZipUmbrella(job)) {
      nested.push({
        job,
        children: (childrenByUmbrellaId.get(job.id) ?? []).sort(bySubmittedAsc)
      })
      continue
    }
    nested.push({ job, children: [] })
  }
  return nested
}
